/* Grubwick backend client for the public menu search (GET /gw/search).
 *
 * Semantic search over embedded menu items. The endpoint returns ranked menu
 * items; a restaurant may appear more than once when several of its dishes
 * match. Raw shapes live in src/api/types.ts per the API typing convention. */
import { gwFetch } from "./client";
import { formatPriceCents, PLACEHOLDER } from "./restaurants";
import { VENUES, type VenueType } from "../data/adminData";
import type { ApiSearchResult, ApiSearchResponse } from "./types";

/* UI-facing hit: one matching menu item and the restaurant serving it.
   `venue`/`address`/`url` are forward-looking — the search API does not return
   them yet, so the card hides address + Visit-site until they arrive. */
export interface MenuSearchHit {
  id: string;
  name: string;
  restaurant: string;
  section: string;
  desc: string;
  price: string;
  venue: VenueType;
  address?: string;
  url?: string;
}

function mapVenue(value: string | null | undefined): VenueType {
  const match = VENUES.find((v) => v === value);
  return match ?? "Restaurant"; // API has no venue type yet — default it.
}

export function mapApiSearchResult(api: ApiSearchResult): MenuSearchHit {
  return {
    id: String(api.item_id),
    name: api.name,
    restaurant: api.restaurant,
    section: api.section ?? PLACEHOLDER,
    desc: api.description ?? "",
    price: formatPriceCents(api.price_cents),
    venue: mapVenue(api.venue),
    address: api.address ?? undefined,
    url: api.website_url ?? undefined,
  };
}

/* Fetches the top `maxResults` matching menu items, ranked best-first. */
export async function searchMenu(
  query: string,
  maxResults = 5,
): Promise<MenuSearchHit[]> {
  const res = await gwFetch(
    `/search?q=${encodeURIComponent(query)}&topk=${maxResults}`,
  );
  if (!res.ok) {
    throw new Error(`Search failed (${res.status})`);
  }
  const data: ApiSearchResponse = await res.json();
  return data.results.map(mapApiSearchResult);
}
